import { type Handle, css } from 'remix/component'
import { buildPackageAppPath } from '@kody-internal/shared/public-urls.ts'
import { routes } from '#universal/routes.ts'
import { formatTimestampDate } from '#client/format-timestamp.ts'
import { on } from '#client/event-mixin.ts'
import {
	colors,
	radius,
	spacing,
	typography,
} from '#universal/styles/tokens.ts'
import {
	ForkAheadLink,
	ForkOutdatedCopyButton,
} from '#universal/fork-outdated-copy-button.tsx'
import { renderIcon } from '#universal/icon.tsx'
import {
	getAccentCalloutCss,
	getGhostButtonCss,
	getPillButtonCss,
	visuallyHiddenCss,
} from '#universal/styles/style-primitives.ts'
import {
	accountDisclosureCss,
	IdValue,
	MetadataGrid,
	TimestampValue,
} from './account-management-components.tsx'
import { RecordChips, recordBodyCss } from './record-table.tsx'
import { AccountPackageDeleteDialog } from './account-package-delete-dialog.tsx'
import { AccountPackageForkAdoption } from './account-package-fork-adoption.tsx'
import {
	type AccountPackageDetail,
	type AccountPackagesLoaderData,
} from '#universal/loader-data.ts'

function isPackageLocked(lockedAt: string | null | undefined) {
	return typeof lockedAt === 'string' && lockedAt.trim().length > 0
}

export function AccountPackageOwnerDetails(
	handle: Handle<{
		ownerUsername: string
		packageDetail: AccountPackageDetail
		lockInFlight: boolean
		onToggleLock: () => void
		onPackagesPayload: (payload: AccountPackagesLoaderData) => void
	}>,
) {
	let confirmName = ''
	let visibilityState: 'idle' | 'submitting' | 'error' = 'idle'
	let visibilityMessage: string | null = null

	async function submitVisibility(
		packageDetail: AccountPackageDetail,
		visibility: 'public' | 'private',
	) {
		if (visibilityState === 'submitting') return
		if (confirmName.trim() !== packageDetail.kodyId) {
			visibilityState = 'error'
			visibilityMessage =
				visibility === 'public'
					? `Type ${packageDetail.kodyId} to make this package public. Anyone will be able to read and fork the default branch.`
					: `Type ${packageDetail.kodyId} to make this package private. Public URLs will 404; existing forks keep their copies.`
			handle.update()
			return
		}
		visibilityState = 'submitting'
		visibilityMessage = null
		handle.update()
		try {
			const response = await fetch('/account/packages.json', {
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				credentials: 'include',
				body: JSON.stringify({
					action: 'set-visibility',
					packageId: packageDetail.id,
					visibility,
					confirmName: confirmName.trim(),
				}),
			})
			const payload = (await response.json()) as AccountPackagesLoaderData & {
				error?: string
			}
			if (!response.ok || !payload?.ok) {
				throw new Error(
					payload?.error ?? 'Could not update package visibility.',
				)
			}
			visibilityState = 'idle'
			confirmName = ''
			handle.props.onPackagesPayload(payload)
			handle.update()
		} catch (error) {
			visibilityState = 'error'
			visibilityMessage =
				error instanceof Error
					? error.message
					: 'Could not update package visibility.'
			handle.update()
		}
	}

	return () => {
		const { packageDetail, lockInFlight, onToggleLock } = handle.props

		return (
			<div mix={css(recordBodyCss)} data-testid="package-owner-details">
				<div mix={css({ display: 'grid', gap: spacing.xs })}>
					<div
						mix={css({
							display: 'flex',
							alignItems: 'center',
							gap: spacing.xs,
							flexWrap: 'wrap',
							minWidth: 0,
						})}
					>
						<h2
							mix={css({
								margin: 0,
								fontSize: typography.fontSize.lg,
								fontWeight: typography.fontWeight.semibold,
								color: colors.text,
								overflowWrap: 'anywhere',
							})}
						>
							{packageDetail.name}
						</h2>
						<button
							type="button"
							disabled={lockInFlight}
							title={
								isPackageLocked(packageDetail.lockedAt)
									? 'Unlock publishes'
									: 'Lock publishes'
							}
							data-testid="account-package-lock-toggle"
							data-locked={
								isPackageLocked(packageDetail.lockedAt) ? 'true' : 'false'
							}
							mix={[
								css(packageLockToggleCss),
								on('click', () => onToggleLock()),
							]}
						>
							{packageLockGlyph(isPackageLocked(packageDetail.lockedAt))}
							<span mix={css(visuallyHiddenCss)}>
								{isPackageLocked(packageDetail.lockedAt)
									? `Unlock publishes for ${packageDetail.name}`
									: `Lock publishes for ${packageDetail.name}`}
							</span>
						</button>
						{packageDetail.listingAhead ? (
							<ForkOutdatedCopyButton
								prompt={packageDetail.listingAhead.prompt}
								testId="account-package-listing-ahead"
								href={packageDetail.listingAhead.diffHref}
							/>
						) : packageDetail.forkAhead ? (
							<ForkAheadLink
								href={packageDetail.forkAhead.diffHref}
								testId="account-package-listing-fork-ahead"
							/>
						) : null}
					</div>
					{packageDetail.description ? (
						<p
							mix={css({
								margin: 0,
								color: colors.textMuted,
								overflowWrap: 'anywhere',
							})}
						>
							{packageDetail.description}
						</p>
					) : (
						<p mix={css({ margin: 0, color: colors.textMuted })}>
							This package has no description.
						</p>
					)}
				</div>
				<div mix={css({ display: 'flex', flexWrap: 'wrap', gap: spacing.xs })}>
					{packageDetail.hidden ? (
						<span mix={css(statusBadgeCss)}>Hidden</span>
					) : null}
					{packageDetail.isPrivate ? (
						<span mix={css(statusBadgeCss)}>Private</span>
					) : null}
					{packageDetail.hasCommunityListing ? (
						<span mix={css(communityBadgeCss)}>Public</span>
					) : (
						<span mix={css(statusBadgeCss)}>Not listed</span>
					)}
				</div>
				<div
					mix={css({ display: 'grid', gap: spacing.xs })}
					data-testid="package-visibility-controls"
				>
					<p mix={css({ margin: 0, color: colors.textMuted })}>
						{packageDetail.isPrivate
							? 'Making this package public lists it on /community. Anyone can read and fork the default branch. Skim source, README, and examples for personal details first, then type the slug to confirm.'
							: 'Making this package private unlists it from /community and 404s public URLs. Existing forks keep their copies. Type the slug to confirm.'}
					</p>
					<label mix={css({ display: 'grid', gap: spacing.xs })}>
						<span mix={css(visuallyHiddenCss)}>
							Type {packageDetail.kodyId} to change visibility
						</span>
						<input
							value={confirmName}
							placeholder={packageDetail.kodyId}
							data-testid={
								packageDetail.isPrivate
									? 'package-make-public-confirm'
									: 'package-make-private-confirm'
							}
							mix={[
								css({
									padding: spacing.sm,
									borderRadius: radius.md,
									border: `1px solid ${colors.border}`,
									backgroundColor: colors.surface,
									color: colors.text,
								}),
								on('input', (event) => {
									if (!(event.currentTarget instanceof HTMLInputElement)) {
										return
									}
									confirmName = event.currentTarget.value
									handle.update()
								}),
							]}
						/>
					</label>
					<button
						type="button"
						disabled={visibilityState === 'submitting'}
						data-testid={
							packageDetail.isPrivate
								? 'package-make-public'
								: 'package-make-private'
						}
						mix={[
							css(getGhostButtonCss()),
							on(
								'click',
								() =>
									void submitVisibility(
										packageDetail,
										packageDetail.isPrivate ? 'public' : 'private',
									),
							),
						]}
					>
						{visibilityState === 'submitting'
							? 'Saving…'
							: packageDetail.isPrivate
								? 'Make public'
								: 'Make private'}
					</button>
					{visibilityMessage ? (
						<p mix={css({ margin: 0, color: colors.danger })} role="alert">
							{visibilityMessage}
						</p>
					) : null}
				</div>
				{packageDetail.tags.length > 0 ? (
					<RecordChips items={packageDetail.tags} />
				) : null}
				<MetadataGrid
					items={[
						{
							label: 'Kody id',
							value: <IdValue value={packageDetail.kodyId} label="Kody id" />,
						},
						{
							label: 'Package id',
							value: <IdValue value={packageDetail.id} label="package id" />,
						},
						{
							label: 'App',
							value: packageDetail.hasApp ? (
								<a
									href={buildPackageAppPath({
										username: handle.props.ownerUsername,
										kodyId: packageDetail.kodyId,
									})}
									data-testid="open-package-app"
									data-rmx-document
									mix={css(packageAppLinkCss)}
								>
									{renderIcon('share', { size: '1em' })}
									Open Package App
								</a>
							) : (
								'No app'
							),
						},
						{
							label: 'Source id',
							value: (
								<IdValue value={packageDetail.sourceId} label="source id" />
							),
						},
						{
							label: 'Created',
							value: <TimestampValue value={packageDetail.createdAt} />,
						},
						{
							label: 'Updated',
							value: <TimestampValue value={packageDetail.updatedAt} />,
						},
						{
							label: 'Publish lock',
							value: packageDetail.lockedAt
								? `Locked ${formatTimestampDate(packageDetail.lockedAt)}`
								: 'Off',
						},
					]}
				/>
				<div mix={css(getAccentCalloutCss())}>
					<p mix={css({ margin: 0, color: colors.textMuted })}>
						{packageDetail.lockedAt
							? 'Publishes stay on this reviewed tree until you promote a commit on the website. Click the lock icon to unlock.'
							: 'Click the lock icon so agents cannot publish without your approval.'}
					</p>
					{packageDetail.lockedAt ? (
						<div
							mix={css({
								display: 'flex',
								flexWrap: 'wrap',
								gap: spacing.xs,
							})}
						>
							<a
								href={routes.communityPackageApprovePublish.href({
									username: handle.props.ownerUsername,
									kodyId: packageDetail.kodyId,
								})}
								data-testid="account-approve-publish"
								mix={css({
									...getPillButtonCss({ size: 'sm' }),
									display: 'inline-flex',
									textDecoration: 'none',
								})}
							>
								Approve a publish
							</a>
						</div>
					) : null}
				</div>
				{packageDetail.communityFork ? (
					<AccountPackageForkAdoption
						packageDetail={packageDetail}
						communityFork={packageDetail.communityFork}
						onPackagesPayload={handle.props.onPackagesPayload}
					/>
				) : null}
				<AccountPackageDeleteDialog
					ownerUsername={handle.props.ownerUsername}
					packageDetail={packageDetail}
				/>
				{packageDetail.searchText ? (
					<details mix={css(accountDisclosureCss)}>
						<summary>Search text</summary>
						<p
							mix={css({
								margin: 0,
								maxHeight: '12rem',
								overflowY: 'auto',
								padding: spacing.sm,
								borderRadius: radius.md,
								border: `1px solid ${colors.border}`,
								backgroundColor: colors.background,
								color: colors.textMuted,
								fontSize: typography.fontSize.sm,
								overflowWrap: 'anywhere',
							})}
						>
							{packageDetail.searchText}
						</p>
					</details>
				) : null}
			</div>
		)
	}
}

function packageLockGlyph(locked: boolean) {
	return renderIcon(locked ? 'lock' : 'lock-unlocked', { size: '1em' })
}

const packageLockToggleCss = {
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '1.85rem',
	height: '1.85rem',
	padding: 0,
	border: `1px solid ${colors.border}`,
	borderRadius: '999px',
	backgroundColor: 'transparent',
	color: colors.textMuted,
	cursor: 'pointer',
	flexShrink: 0,
	'&:hover': {
		color: colors.primaryText,
		borderColor: colors.primaryText,
	},
	'&:focus-visible': {
		outline: `2px solid ${colors.primary}`,
		outlineOffset: '2px',
	},
	'&[data-locked="true"]': {
		color: colors.primary,
		borderColor: colors.primary,
		backgroundColor: `oklch(from ${colors.primary} l c h / 0.13)`,
		'&:hover': {
			color: colors.primaryText,
			borderColor: colors.primaryText,
			backgroundColor: `oklch(from ${colors.primary} l c h / 0.2)`,
		},
	},
}

const statusBadgeCss = {
	padding: `${spacing.xs} ${spacing.sm}`,
	borderRadius: radius.full,
	backgroundColor: colors.surface,
	border: `1px solid ${colors.border}`,
	color: colors.textMuted,
	fontSize: typography.fontSize.xs,
	fontWeight: typography.fontWeight.medium,
}

const communityBadgeCss = {
	...statusBadgeCss,
	backgroundColor: colors.primarySoft,
	border: 'none',
	color: colors.primaryText,
	fontWeight: typography.fontWeight.semibold,
}

const packageAppLinkCss = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: '0.35rem',
	color: colors.primaryText,
	fontWeight: typography.fontWeight.semibold,
	textDecoration: 'none',
	'&:hover': {
		color: colors.text,
	},
}
